/**
 * Usage History View Component
 * Displays user's usage events with pagination
 */

import React, { useEffect, useState } from 'react';
import { apiClient, type UsageResponse } from '../services/apiClient';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatNumber, formatDecimal, formatDateTime } from '../utils/persian';

export const UsageView: React.FC = () => {
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadUsage(1);
  }, []);

  const loadUsage = async (pageNum: number) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const data = await apiClient.getUsage(pageNum, 20);
      
      if (pageNum === 1) {
        setUsage(data);
      } else {
        // Append to existing events
        setUsage(prev => prev ? {
          ...data,
          usage: [...prev.usage, ...data.usage],
        } : data);
      }
      
      setPage(pageNum);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در بارگذاری تاریخچه مصرف';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (usage && usage.usage.length < usage.total) {
      loadUsage(page + 1);
    }
  };

  const getEventTypeLabel = (eventType: string): string => {
    const labels: Record<string, string> = {
      'transcription': 'رونویسی',
      'ai_feature': 'ویژگی هوش مصنوعی',
      'manual_deduction': 'کسر دستی',
    };
    return labels[eventType] || eventType;
  };

  const getEventIcon = (eventType: string): string => {
    const icons: Record<string, string> = {
      'transcription': '🎙️',
      'ai_feature': '🤖',
      'manual_deduction': '➖',
    };
    return icons[eventType] || '📝';
  };

  if (loading) {
    return <LoadingSpinner message="در حال بارگذاری تاریخچه مصرف..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => loadUsage(1)} />;
  }

  if (!usage || usage.usage.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={styles.emptyIcon}>📊</p>
        <p style={styles.emptyText}>هنوز هیچ مصرفی ثبت نشده است</p>
        <p style={styles.emptySubtext}>
          پس از استفاده از سرویس رونویسی، تاریخچه مصرف شما اینجا نمایش داده می‌شود.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>تاریخچه مصرف</h2>
        <p style={styles.subtitle}>
          {formatNumber(usage.total)} مورد
        </p>
      </div>

      <div style={styles.eventList}>
        {usage.usage.map((event) => (
          <div key={event.id} style={styles.eventCard}>
            <div style={styles.eventHeader}>
              <div style={styles.eventIcon}>{getEventIcon(event.type)}</div>
              <div style={styles.eventInfo}>
                <p style={styles.eventType}>{getEventTypeLabel(event.type)}</p>
                <p style={styles.eventDate}>{formatDateTime(event.created_at)}</p>
              </div>
              <div style={styles.eventCredits}>
                <p style={styles.creditsValue}>-{formatNumber(event.credits_deducted)}</p>
                <p style={styles.creditsLabel}>واحد</p>
              </div>
            </div>

            <div style={styles.eventDetails}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>معادل دقیقه:</span>
                <span style={styles.detailValue}>{formatDecimal(event.equivalent_minutes ?? 0, 2)}</span>
              </div>

              {event.transcription_id && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>شناسه رونویسی:</span>
                  <span style={styles.detailValue}>{event.transcription_id.substring(0, 8)}...</span>
                </div>
              )}

              {event.duration_sec !== undefined && event.duration_sec !== null && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>مدت زمان:</span>
                  <span style={styles.detailValue}>
                    {formatDecimal(event.duration_sec / 60, 1)} دقیقه
                  </span>
                </div>
              )}

              {event.feature_type && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>نوع ویژگی:</span>
                  <span style={styles.detailValue}>{event.feature_type}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {usage.usage.length < usage.total && (
        <div style={styles.loadMoreContainer}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={styles.loadMoreButton}
          >
            {loadingMore ? 'در حال بارگذاری...' : 'موارد بیشتر'}
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  eventList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  eventCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  eventIcon: {
    fontSize: '1.5rem',
  },
  eventInfo: {
    flex: 1,
  },
  eventType: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  eventDate: {
    fontSize: '0.75rem',
    color: '#999',
    margin: 0,
  },
  eventCredits: {
    textAlign: 'left',
  },
  creditsValue: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#f44336',
    margin: '0 0 0.25rem 0',
  },
  creditsLabel: {
    fontSize: '0.625rem',
    color: '#999',
    margin: 0,
  },
  eventDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #f0f0f0',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
  },
  detailLabel: {
    color: '#666',
  },
  detailValue: {
    color: '#1a1a1a',
    fontWeight: 500,
  },
  loadMoreContainer: {
    marginTop: '1rem',
    textAlign: 'center',
  },
  loadMoreButton: {
    backgroundColor: '#646cff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  emptyState: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '3rem 1.5rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '3rem',
    margin: '0 0 1rem 0',
  },
  emptyText: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.5rem 0',
  },
  emptySubtext: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: 1.6,
  },
};
